import React, {FC, useMemo} from "react";
import {Alert} from "antd";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {eventWorkspaceRoute} from "../eventWorkspaceRoutes";
import {ParticipantDetail} from "./ParticipantDetail";

type ParticipantDetailLocationState = {
    fallbackName?: string;
};

const parsePositiveInt = (value?: string) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const ParticipantDetailPage: FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {eventId: routeEventId, participantId: routeParticipantId} = useParams();
    const state = location.state as ParticipantDetailLocationState | null;

    const eventId = parsePositiveInt(routeEventId);
    const participantId = parsePositiveInt(routeParticipantId);
    const fallbackName = state?.fallbackName?.trim()
        || t("participant-detail", {defaultValue: "Participant detail"});

    const target = useMemo(() => {
        if (!eventId || !participantId) {
            return undefined;
        }

        return {
            eventId,
            participantId,
            fallbackName
        };
    }, [eventId, fallbackName, participantId]);

    if (!target) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t("participant-detail", {defaultValue: "Participant detail"})}
                description={t("participant-detail.invalid", {defaultValue: "Invalid participant detail route."})}
            />
        );
    }

    return (
        <ParticipantDetail
            target={target}
            onDeleted={() => navigate(eventWorkspaceRoute(target.eventId, "participants"))}
        />
    );
};

export default ParticipantDetailPage;
